
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { Validate } from '@/types';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import TextField from '@/components/shared/TextField.vue';
import Tooltip from '@/components/shared/Tooltip.vue';
import DeliveryAddress from './delivery/Address.vue';
import DeliveryStadium from './delivery/Stadium.vue';
import DeliverySchool from './delivery/School.vue';
import DeliveryCustom from './delivery/Custom.vue';
import DeliveryFeeMode from './delivery/FeeMode.vue';
import BannerToast from '@/components/shared/BannerToast.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import SelectComponent from '@/components/shared/Select.vue';
import '@/validation-rules';

const namespace: string = 'auth';

@Component<DeliveryModal>({
	components: {
		SelectComponent,
		ValidationObserver,
		ValidationProvider,
		StandardModal,
		VueGoogleAutocomplete,
		TextField,
		Tooltip,
		DeliveryAddress,
		DeliveryStadium,
		DeliverySchool,
		DeliveryCustom,
		DeliveryFeeMode,
		BannerToast,
		InfoBanner
	}
})
export default class DeliveryModal extends Vue {
	@Action('updateRestaurant', { namespace }) private updateRestaurant!: (restaurant: Restaurant | object) => Promise<void>;
	@Getter('getCurrencySymbol', { namespace }) private currencySymbol!: string;
	@Getter('areCateringEventsEnabled', { namespace }) private areCateringEventsEnabled!: boolean;
	@Prop({ type: Object, default: () => {} }) private restaurant!: Restaurant;
	@Prop({ type: Object, default: () => {} }) private targetDeliveryInfo!: Delivery;
	@Prop({ type: Number, default: 2 }) private targetDeliveryChargeTypeIsDollar!: number;
	@Prop({ type: Boolean, default: false }) private targetDeliveryEnabled!: boolean;
	@Prop({ type: Boolean, required: true, default: false }) private showModal!: boolean;
	@Watch('mutableDeliveryEnabled')
	onMutableDeliveryEnabledChange() {
		if (!this.mutableDeliveryEnabled) {
			this.deliveryChargeEnabled = false;
			this.mutableDeliveryInfo = {} as Delivery;
		}
	}
	@Watch('deliveryChargeEnabled')
	onDeliveryChargeEnabledChange() {
		if (!this.deliveryChargeEnabled) {
			this.mutableDeliveryCharge = '';
		}
		if(this.mutableDeliveryChargeTypeIsDollar === 2) {
			this.mutableDeliveryChargeTypeIsDollar = 0;
		}
	}
	/**
	 * Update fee range suffixes when the charge type ($/%) is toggled
	 *
	 * @return {void}
	 */
	@Watch('mutableDeliveryChargeTypeIsDollar')
	onChargeTypeChanged(): void {
		if (this.deliveryFeeRanges.length) {
			const newSuffix = this.mutableDeliveryChargeTypeIsDollar === 0 ? '$' : '%';
			this.deliveryFeeRanges = this.deliveryFeeRanges.map(range => {
				if (!range.fee) {
					return range;
				}
				const value = range.fee.replace(/[$%]$/, '');
				return { ...range, fee: `${value}${newSuffix}` };
			});
		}
	}

	$refs!: { observer: Validate; };

	private mutableDeliveryEnabled: boolean = this.targetDeliveryEnabled;
	private deliveryChargeEnabled: boolean = false;
	private loading: boolean = false;
	private mutableDeliveryCharge: string = '';
	private mutableDeliveryChargeTypeIsDollar: number = this.targetDeliveryChargeTypeIsDollar;
	private mutableDeliveryInfo: Delivery = this.targetDeliveryInfo;
	private deliveryFeeRanges: DeliveryFeeRange[] = [];
	private deliveryFeeMode: 'fixed' | 'custom' = 'fixed';
	private deliveryTypes: { label: string, value: string }[] = [
		{ label: this.$t('settings.delivery.types.address'), value: 'address' },
		{ label: this.$t('settings.delivery.types.open_address'), value: 'open_address' },
		{ label: this.$t('settings.delivery.types.stadium'), value: 'stadium' },
		{ label: this.$t('settings.delivery.types.school'), value: 'school' },
		{ label: this.$t('settings.delivery.types.custom'), value: 'custom' }
	];
	private activeTab: number = 0;
	private cateringEventDeliveryType: 'address' | 'open_address' = this.restaurant?.order_options?.catering_events_delivery_type || 'address';
	private cateringEventDeliveryTypes: { label: string, value: string }[] = [
		{ label: this.$t('settings.delivery.types.address'), value: 'address' },
		{ label: this.$t('settings.delivery.types.open_address'), value: 'open_address' }
	];

	private mounted(): void {
		if(this.mutableDeliveryInfo?.delivery_charge) {
			this.deliveryChargeEnabled = true;
			this.mutableDeliveryCharge = this.mutableDeliveryInfo.delivery_charge.slice(0, - 1);
			this.$nextTick();
		}

		// Initialize custom fee ranges from existing data
		if (this.mutableDeliveryInfo?.delivery_fee_type === 'custom') {
			this.deliveryFeeMode = 'custom';
			this.deliveryFeeRanges = [...(this.mutableDeliveryInfo.delivery_fee_ranges || [])];
			this.deliveryChargeEnabled = true;
		}
	}

	/**
	 * We validate the delivery depending on the type and then assign
	 * the values that we're not from the inputs if everything was
	 * valid
	 *
	 * @return {boolean}
	 */
	private validateDelivery(): boolean {
		if (this.mutableDeliveryEnabled && this.mutableDeliveryInfo) {
			// Validate geolocation
			if (this.mutableDeliveryInfo.type === 'address') {
				if (!this.validateDeliveryAddress()) {
					return false;
				}
			}

			// Validate custom
			if (this.mutableDeliveryInfo.type === 'custom') {
				if (!this.validateDeliveryCustom()) {
					return false;
				}
			}

			// Validate school
			if (this.mutableDeliveryInfo.type === 'school' && !this.validateSchoolDelivery()) {
				return false;
			}

			// Validate custom fee ranges
			if (this.deliveryChargeEnabled && this.mutableDeliveryInfo.type === 'address' && this.deliveryFeeMode === 'custom') {
				const radius = Number(this.mutableDeliveryInfo.radius) || 0;
				const lastRangeTo = this.deliveryFeeRanges.length ? this.deliveryFeeRanges[this.deliveryFeeRanges.length - 1].to : 0;

				if (!this.deliveryFeeRanges.length || this.deliveryFeeRanges.some(r => !r.fee)) {
					Toast.error(this.$t('settings.delivery.custom_fee_no_ranges'));
					return false;
				}

				if (lastRangeTo < radius) {
					Toast.error(this.$t('settings.delivery.custom_fee_incomplete_radius'));
					return false;
				}
			}

			this.mutableDeliveryInfo.type = this.mutableDeliveryInfo.type?.toLowerCase();
			return true;
		}
		else {
			this.mutableDeliveryInfo = {} as Delivery;
			return true;
		}
	}

	/**
	 * Validate address delivery
	 * We essentially make sure that the autocomplete worked properly
	 * meaning we do have lat, lng, address and country which are crucial
	 *
	 * @return {boolean}
	 */
	private validateDeliveryAddress(): boolean {
		this.mutableDeliveryInfo.radius = +this.mutableDeliveryInfo.radius!;
		if (!this.mutableDeliveryInfo.lat || !this.mutableDeliveryInfo.lng || !this.mutableDeliveryInfo.address || !this.mutableDeliveryInfo.country) {
			Toast.error(this.$t('settings.error_address'));
			return false;
		}
		return true;
	}

	/**
	 * Validate custom delivery
	 * We essentially make sure that the object properties in the array (location, areas)
	 * are not empty, if they are, we slice them.
	 *
	 * @return {boolean}
	 */
	private validateDeliveryCustom(): boolean {
		this.mutableDeliveryInfo.custom = this.mutableDeliveryInfo.custom!.filter((location: CustomDeliveryElements) => location.location && location.areas);

		if (!this.mutableDeliveryInfo.areas_label && !this.mutableDeliveryInfo.location_label && !this.mutableDeliveryInfo.custom.length) {
			return false;
		}
		return true;
	}

	/**
	 * Validate school delivery.
	 * We essentially make sure that every school has a unique name.
	 *
	 * @return {boolean}
	 */
	private validateSchoolDelivery(): boolean {
		const schoolNameSet = new Set(this.mutableDeliveryInfo.schools!.map(school => school.name));
		if (schoolNameSet.size !== this.mutableDeliveryInfo.schools!.length) {
			Toast.error(this.$t('settings.error_school_name'));
			return false;
		}
		return true;
	}

	/**
	 * On delivery type changed, we wanna make sure to reset the delivery info
	 *
	 * @return {void}
	 */
	private deliveryTypeChanged(): void {
		// Reset fee mode when switching away from address
		if (this.mutableDeliveryInfo.type !== 'address') {
			this.deliveryFeeMode = 'fixed';
		}

		if (this.mutableDeliveryInfo.type === 'address') {
			this.mutableDeliveryInfo = {
				type: 'address',
				address: '',
				country: '',
				lat: 0,
				lng: 0,
				radius: 0
			};
		}
		else if (this.mutableDeliveryInfo.type === 'open_address') {
			this.mutableDeliveryInfo = {
				type: 'open_address'
			};
		}
		else if (this.mutableDeliveryInfo.type === 'stadium') {
			this.mutableDeliveryInfo = {
				type: 'stadium',
				sections: '',
				rows: '',
				seats: ''
			};
		}
		else if (this.mutableDeliveryInfo.type === 'school') {
			this.mutableDeliveryInfo = {
				type: 'school',
				schools: [{ id: Date.now(), name: '' }]
			};
		}
		else {
			this.mutableDeliveryInfo = {
				type: 'custom',
				custom: [{ location: '', areas: '' }]
			};
		}
	}

	/**
	 * Build the delivery object that will be saved depending of its type.
	 *
	 * @return {Delivery}
	 */
	private buildDeliveryPayload(): Delivery {
		const deliveryPayload = cloneDeep(this.mutableDeliveryInfo);

		if (deliveryPayload.type === 'school') {
			deliveryPayload.schools = deliveryPayload.schools?.map(({ isTeachersOpenEnded, ...rest }) => rest);
		}

		// Set delivery charge with suffix
		if (this.deliveryChargeEnabled) {
			deliveryPayload.delivery_charge = this.mutableDeliveryCharge ? `${this.mutableDeliveryCharge}${this.mutableDeliveryChargeTypeIsDollar === 0 ? '$' : '%'}` : '';
		}
		else {
			deliveryPayload.delivery_charge = '';
		}

		// Set delivery fee fields for address type only
		if (this.deliveryChargeEnabled && deliveryPayload.type === 'address' && this.deliveryFeeMode === 'custom') {
			deliveryPayload.delivery_fee_type = 'custom';
			deliveryPayload.delivery_fee_ranges = [...this.deliveryFeeRanges];
			deliveryPayload.delivery_charge = '';
		}
		else if (deliveryPayload.type === 'address') {
			deliveryPayload.delivery_fee_type = 'fixed';
			delete deliveryPayload.delivery_fee_ranges;
		}

		return deliveryPayload;
	}

	/**
	 * Save the delivery info here. We first validate then we try to save
	 *
	 * @return {Promise<void>}
	 */
	private async save(): Promise<void> {
		this.loading = true;
		const isValid = await this.$refs.observer.validate();
		let deliveryPayload: Delivery | null = null;

		if (!isValid || !this.validateDelivery()) {
			// Validation errors render on the Delivery tab's inputs, so switch back to it in case the user saved from the Events tab
			this.activeTab = 0;
			if (!isValid) {
				Toast.error(this.$t('settings.delivery.invalid_settings'));
			}
		}
		else {
			try {
				deliveryPayload = this.buildDeliveryPayload();
				const restaurantPayload: Partial<Restaurant> = { id: this.restaurant.id, delivery: deliveryPayload };
				if (this.areCateringEventsEnabled) {
					restaurantPayload.order_options = { ...this.restaurant.order_options, catering_events_delivery_type: this.cateringEventDeliveryType };
				}
				await this.updateRestaurant({ restaurant: restaurantPayload });
			}
			catch (errorMessage) {
				Toast.error(this.$t('settings.delivery.error_saving', { errorMessage }));
				deliveryPayload = null;
			}
		}

		if (deliveryPayload) {
			this.$emit('update-delivery-info', { enabled: this.mutableDeliveryEnabled, info: deliveryPayload, type: this.mutableDeliveryChargeTypeIsDollar });
		}
		this.loading = false;
	}

	/**
	 * Emit event to close modal
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}
}
